import { create } from 'zustand'
import type { WebinarRecord, WebinarStatus } from '../models/webinar.model'
import {
  resolveWebinarStatus,
  webinarsService,
} from '../services/webinar.service'
import { webinarRepo } from '../services/webinar.repo'

/**
 * Reactive cache over `webinarsService`. The repo (localStorage) is the
 * source of truth; this store hydrates from it on first use and refreshes
 * on every mutation. UI components subscribe via selectors; all writes go
 * through the named actions.
 *
 * Legacy callers still expect a `StoredWebinar` shape — kept as a type alias
 * over `WebinarRecord` so the existing list/detail components don't break.
 */

export type { WebinarStatus }
export type StoredWebinar = WebinarRecord
export { resolveWebinarStatus }

interface WebinarStoreState {
  webinars: WebinarRecord[]
  hydrated: boolean

  hydrate: () => void
  refresh: () => void
  /** Accepts both full WebinarRecord and the legacy slim shape — repo
   *  normalises missing fields. Prefer `webinarsService.create()` when
   *  composing new records from user input. */
  addWebinar: (webinar: Partial<WebinarRecord> & { id: string }) => void
  updateWebinar: (id: string, patch: Partial<WebinarRecord>) => void
  removeWebinar: (id: string) => void
  refreshStatuses: () => void
}

const reload = (): WebinarRecord[] =>
  webinarRepo.all().map((w) => ({
    ...w,
    status:
      w.status === 'draft' || w.status === 'cancelled'
        ? w.status
        : resolveWebinarStatus(w.startTimeMs, w.endTimeMs),
  }))

export const useWebinarStore = create<WebinarStoreState>((set) => ({
  webinars: typeof window === 'undefined' ? [] : reload(),
  hydrated: typeof window !== 'undefined',

  hydrate: () => set({ webinars: reload(), hydrated: true }),

  refresh: () => set({ webinars: reload() }),

  addWebinar: (webinar) => {
    if (!webinarRepo.findById(webinar.id)) {
      // bulkReplace passes records through the repo's normaliser on the next
      // read, so we just save the slim shape as-is. Subsequent reads will
      // backfill any missing nested settings with safe defaults.
      const existing = webinarRepo.all()
      webinarRepo.bulkReplace([webinar as WebinarRecord, ...existing])
    }
    set({ webinars: reload() })
  },

  updateWebinar: (id, patch) => {
    webinarRepo.update(id, patch)
    set({ webinars: reload() })
  },

  removeWebinar: (id) => {
    webinarsService.remove(id)
    set({ webinars: reload() })
  },

  refreshStatuses: () => {
    webinarsService.refreshStatuses()
    set({ webinars: reload() })
  },
}))
